import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import Pagination from 'react-ultimate-pagination-bootstrap-4';
import { compose } from 'redux';
import {faExclamationTriangle} from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle";
import moment from 'moment';
import { users } from '../../ducks/index';

import queryBuilder from 'utils/queryBuilder';

import withFiltering from 'hocs/withFiltering';
import withPagination from 'hocs/withPagination';
import withSchoolYear, {defaultBillingSchoolYear} from 'hocs/withSchoolYear';
import withSorting from 'hocs/withSorting';

import ContactList from 'components/ContactList/ContactList';
import BulkActions from 'components/ContactList/BulkActions/BulkActions';
import AddCharge from 'components/ContactList/BulkActions/AddCharge';
import AddCredit from 'components/ContactList/BulkActions/AddCredit';
import Edit from 'components/ContactList/BulkActions/Edit';
import CommunicationMenu from 'components/ContactList/BulkActions/CommunicationMenu';
import SummaryTotalsHeader from 'components/ContactList/SummaryTotalsHeader';

import Filters from 'components/Filters/Filters';
import FlashRoot from 'components/FlashMessage/FlashRoot';
import FontAwesome from "utils/FA";

import SchoolYearSelect from 'components/SchoolYearSelect';
import LinkButton from 'components/LinkButton/LinkButton';

import {
  dependentDuck,
  duckify,
  filterDuck,
  singleDuck
} from 'utils/duckify';

import {
  billingCategoryGroups as billingCategoriesDuck,
  billingStudents,
  bulkAddTransactionJobs,
  customers as customersDuck,
  fields as fieldsDuck,
  flashMessages,
  modals,
  schoolYears as schoolYearsDuck
} from 'ducks';

import './styles/StudentsList.scss';
import useBulkActions from 'hooks/useBulkActions';
import stringParams from "utils/stringParams";
import SaPropTypes from 'propTypes';
import { faSignOut } from '@fortawesome/pro-solid-svg-icons/faSignOut';
import {
  bulkCheckboxColumn,
  currencyColumn,
  dateColumn,
  defaultColumn,
  linkColumn
} from '../ContactList/ColumnDefinitions';
import BulkAction from '../ContactList/BulkActions/BulkAction';
import BulkExportAction from '../ContactList/BulkActions/BulkExportAction';
const customerDuck = singleDuck(customersDuck, 'current');

const StudentsListPage = ({
  addTransaction,
  billingCategories,
  contacts,
  fields,
  additionalFields,
  filters,
  hideModal,
  onSchoolYearChange,
  onSort,
  page,
  schoolYearId,
  schoolYears,
  setFilters,
  setPage,
  showFlash,
  showModal,
  sort,
  location,
  currentUser,
}) => {
  const totalPages = Math.ceil(contacts.total / contacts.perPage);

  const { trackedItems,
    selectionModeCount,
    promptSelectAll,
    setPromptSelectAll,
    selectionMode,
    togglePageTracked,
    toggleTrackedItem,
    itemIsSelected,
    selectAllItems } = useBulkActions(contacts);

  const [filteredSchoolYears, setYears] = useState(schoolYears);

  const summaryColumns = [
    { name: 'next_contact_balance', publicName: 'Next Amount' },
    { name: 'total_contact_balance', publicName: 'Remaining Amount' },
    { name: 'current_contact_balance', publicName: 'Current Amount' }
  ];

  // We only want years that are relevant for billing
  useEffect(() => {
    const requestedYear = schoolYears.find(year => year.id === schoolYearId)

    if (requestedYear && requestedYear.archived) {
      const availableYear = schoolYears.find(year => year.default_for_reporting)
      showFlash({
        message: `The selected term ${requestedYear.name} has been archived. You've been redirected to the current term.`,
        type: 'warning'
      });
      onSchoolYearChange(availableYear.id)
      setYears(schoolYears.filter(year => (year.billing_enabled && !year.archived)  || year.id === availableYear.id));
    }
    else {
      setYears(schoolYears.filter(year => (year.billing_enabled && !year.archived)  || year.id === schoolYearId));
    }
  }, [schoolYears]);

  let additionalColumns = [];

  if (contacts.ready) {
    additionalColumns = contacts.additionalFields.map(column => ({
      header: column.print_name,
      type: column.react_type,
      sortName: column.sort_name,
      value: (student) => (
        <td
          className={`contact-list-react-type-${column.react_type || 'text'}`}
          dangerouslySetInnerHTML={{__html: student.additional_fields[column.canonical_name]}}
        />
      )
    }));
  }

  /** The list of columns to show for this page */
  const columns = [
    bulkCheckboxColumn({
      value: 'id',
      promptSelectAll,
      togglePageTracked,
      selectionMode,
      itemIsSelected,
      toggleTrackedItem
    }),
    linkColumn({
      header: 'Name',
      sortName: 'display_name',
      type: 'name',
      value: (student) => student.display_name,
      link: (student) => `/contacts/${student.id}/billing?school_year_id=${schoolYearId}`
    }),
    defaultColumn({
      header: 'Payment Plan',
      sortName: 'billing_payment_plan[]',
      value: 'billing_payment_plan'
    }),
    defaultColumn({
      header: 'Grade',
      sortName: 'enroll_grade_num',
      value: 'grade'
    }),
    ...additionalColumns,
    dateColumn({
      header: 'Next Date',
      value: (student) => (
        <td className='contact-list-react-type-date'>
          {moment(student.next_billing_date).format('MMMM YYYY')}
        </td>
      )
    }),
    currencyColumn({
      header: 'Next Amount',
      sortName: 'next_contact_balance[]',
      value: 'next_contact_balance'
    }),
    currencyColumn({
      header: 'Remaining',
      sortName: 'total_contact_balance[]',
      type: 'currency',
      value: 'total_contact_balance'
    }),
    currencyColumn({
      header: 'Current Balance',
      sortName: 'current_contact_balance[]',
      type: 'currency',
      value: 'current_contact_balance'
    }),
    defaultColumn({
      header: 'Overdue',
      sortName: 'billing_contact_overdue',
      type: 'overdue-flag',
      value: (student) => (
        <td className="contact-list-react-type-overdue-flag">
          {student.billing_contact_overdue && <FontAwesome icon={faExclamationTriangle} className='red' postNbsp/>}
        </td>
      )
    })
  ];

  return (
    <div className='billing-students-list'>
      <FlashRoot/>
      <div className='contact-list-react-page-header'>
        <div className='contact-list-react-page-title'>
          <h2 className='inline'>
            Students for
          </h2>
          &nbsp;
          {
            schoolYears.ready && (
              <SchoolYearSelect
                onSelect={(year) => onSchoolYearChange(year)}
                schoolYearId={schoolYearId}
                schoolYears={filteredSchoolYears}
              />
            )
          }
        </div>

        <BulkActions
          search={contacts.search}
          contactTotal={contacts.total}
          filterType={selectionMode}
          filterContactIds={trackedItems}
          showFlash={(props) => showFlash(props)}
          showModal={(name, props) => showModal(name, props)}
          hideModal={() => hideModal()}
        >
          <BulkAction title='Export' icon={faSignOut}>
            <BulkExportAction
              type='StudentReportJob'
              location={location}
              url='/api/billing_students/export.csv'
              schoolYearId={schoolYearId}
            />
            <BulkExportAction
              title='Export to PDF'
              type='StudentReportJob'
              location={location}
              url='/api/billing_students/export.pdf'
              schoolYearId={schoolYearId}
            />
          </BulkAction>
          <CommunicationMenu disabled={!contacts.ready || selectionModeCount() === 0} schoolYearId={schoolYearId}
                             active={currentUser.communications_permitted}/>
          <Edit disabled={!contacts.ready || !billingCategories.ready || selectionModeCount() === 0}>
            <AddCharge
              addTransaction={addTransaction}
              billingCategories={billingCategories}
              count={selectionModeCount()}
              schoolYearId={schoolYearId}
            />
            <AddCredit
              addTransaction={addTransaction}
              billingCategories={billingCategories}
              count={selectionModeCount()}
              schoolYearId={schoolYearId}
            />
          </Edit>
        </BulkActions>
      </div>

      {promptSelectAll && (
        <div className={'contact-list-react-prompt-select-all'}>
          <span>You have selected <strong>{selectionModeCount()}</strong> contacts.</span>
          &nbsp;
          <LinkButton
            onClick={selectAllItems}
            content={<span>Select all <strong>{contacts.total}</strong> contacts across all pages.</span>}
          />
        </div>
      )}

      <SummaryTotalsHeader apiPath={'api/billing/summary?' + stringParams({school_year_id: schoolYearId})} columns={summaryColumns} />

      <div className='students-space-between'>
        {(fields.length + additionalFields.length) > 0 && (
          <Filters
            fields={fields.concat(additionalFields)}
            selectCount={selectionModeCount()}
            count={contacts.total}
            selected={filters}
            submitFilters={setFilters}
            itemsName='contacts'
          />
        )}

        {totalPages > 1 && (
          <div>
            <div className='contact-list-react-pagination'>
              <div className='pagination pagination-right'>
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  hideFirstAndLastPageLinks
                  onChange={(newPage) => {
                    setPage(newPage);
                    setPromptSelectAll(false);
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <ContactList
        columns={columns}
        contacts={contacts}
        emptyMessage='There are no billing students for this year.'
        onSort={onSort}
        sort={sort}
      />
    </div>
  );
};

StudentsListPage.propTypes = {
  /** Adds a transaction */
  addTransaction: PropTypes.func,

  /** The list of contacts */
  billingCategories: PropTypes.arrayOf(PropTypes.shape({id: PropTypes.string})),

  /** The list of contacts */
  contacts: PropTypes.arrayOf(PropTypes.object),

  /** The current customer */
  customer: PropTypes.shape({
    current_enrollment_year: PropTypes.shape({
      id: PropTypes.string
    })
  }),

  /** The default fields that are available to filter by */
  fields: PropTypes.arrayOf(PropTypes.object),

  /** Additional fields that are available to filter by */
  additionalFields: PropTypes.arrayOf(PropTypes.object),

  /** The filters selected for this page */
  filters: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(PropTypes.string)
  }),

  /** Hides all modals */
  hideModal: PropTypes.func,

  /** Method fired when the school year selector is changed */
  onSchoolYearChange: PropTypes.func,

  /** Used to generate a link to sort by a given column */
  onSort: PropTypes.func,

  /** The current page */
  page: PropTypes.number.isRequired,

  /** The current school year id */
  schoolYearId: PropTypes.string,

  /** School years for track siloing */
  schoolYears: PropTypes.arrayOf(SaPropTypes.SchoolYear),

  /** Sets filters to a given filterset */
  setFilters: PropTypes.func,

  /** Sets page to a given page */
  setPage: PropTypes.func,

  /** Shows a flash message */
  showFlash: PropTypes.func,

  /** Shows a modal */
  showModal: PropTypes.func,

  /** The sort selected for this page */
  sort: PropTypes.shape({
    column: PropTypes.string,
    order: PropTypes.string
  }),

  location: PropTypes.shape({
    search: PropTypes.string
  })
};

StudentsListPage.defaultProps = {
  billingCategories: [],
  contacts: [],
  customer: {},
  fields: [],
  additionalFields: [],
  filters: {},
  schoolYearId: '',
  schoolYears: [],
  sort: {},
  location: {},

  addTransaction: () => false,
  hideModal: () => false,
  onSchoolYearChange: () => false,
  onSort: () => false,
  setFilters: () => false,
  setPage: () => false,
  showFlash: () => false,
  showModal: () => false
};

const mapDucksToProps = ({ location }) => ({
  billingCategories: filterDuck(billingCategoriesDuck, { active: true, contact_only: true }),
  contacts:
    dependentDuck(customerDuck,
      ({ value }) => queryBuilder(
        defaultBillingSchoolYear({customer: value}), billingStudents, location
      )),
  customer: customerDuck,
  fields: filterDuck(fieldsDuck, { solr_prompt: ['enroll_grade_id', 'billing_contact_overdue'] }),
  additionalFields: filterDuck(fieldsDuck, {for: 'billing'}),
  schoolYears: schoolYearsDuck,
  currentUser: singleDuck(users, 'current')
});

const mapDispatchToProps = (dispatch) => ({
  addTransaction: (transaction) => dispatch(bulkAddTransactionJobs.actions.create(transaction)),
  showFlash: (props) => dispatch(flashMessages.actions.show(props)),
  showModal: (name, props) => dispatch(modals.actions.show(name, props)),
  hideModal: () => dispatch(modals.actions.hide())
});

export default compose(
  withRouter,
  withFiltering,
  withPagination,
  withSorting({ column: 'display_name', order: 'desc' }),
  duckify(mapDucksToProps, () => ({}), mapDispatchToProps),
  withSchoolYear(defaultBillingSchoolYear)
)(StudentsListPage);
